import { useCallback, useState } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import Lottie from "lottie-react";
import animationData from "../../assets/404-animation.json";
import { motion, AnimatePresence } from "framer-motion";
import type { TextToSpeechErrorType } from "@/types/text-to-speech";
import type { AIError } from "@/types/ai";
import { Loader2 } from "lucide-react";
import { useHapticFeedback } from "@/hooks/use-haptic";

interface ErrorPageProps {
  code: string;
  title: string;
  subtitle: string;
  error?: AIError | { type: TextToSpeechErrorType; error: string };
  timestamp?: string;
  onRetry?: () => void;
}

function getErrorMessage(error: ErrorPageProps['error']): string {
  if (!error) return '';

  if ('type' in error) {
    switch (error.type) {
      case 'INVALID_API_KEY':
        return 'API key validation failed. Please check your configuration.';
      case 'RATE_LIMIT_EXCEEDED':
        return 'Too many requests. Please try again later.';
      case 'INVALID_VOICE_ID':
        return 'The selected voice is not available.';
      case 'SERVICE_ERROR':
        return 'An unexpected service error occurred.';
      default:
        return error.error;
    }
  }

  return error.error;
}

export function ErrorPage({ code, title, subtitle, error, timestamp, onRetry }: ErrorPageProps) {
  const errorMessage = error ? getErrorMessage(error) : '';
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStep, setProcessingStep] = useState(0);
  const { triggerHaptic } = useHapticFeedback();

  const handleHomeClick = useCallback(async () => {
    try {
      await triggerHaptic("warning");
      setIsProcessing(true);

      // Clear localStorage to remove potential problematic data
      try {
        setProcessingStep(1);
        localStorage.removeItem('runtimeErrors');
        sessionStorage.removeItem('runtimeErrors');
        console.log('[MJ] Runtime errors cleared from storage');
        setProcessingStep(2);
      } catch (error) {
        console.error('[MJ] Storage clearing failed:', error);
      }

      // Set processing step to 4 (skipping steps since service workers have been removed)
      setProcessingStep(4);

      // Force reload after a short delay
      setTimeout(() => {
        window.location.href = '/';
      }, 1500);
    } catch (error) {
      console.error("[MJ] Error during home click:", error);
      // Even if haptic fails, still proceed with reload
      setTimeout(() => {
        window.location.href = '/';
      }, 1500);
    }
  }, [triggerHaptic]);

  const getProcessingText = () => {
    switch (processingStep) {
      case 1:
        return "Clearing runtime data...";
      case 2:
        return "Data cleared successfully...";
      case 4:
        return "Preparing for reload...";
      default:
        return "Processing...";
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[100vh] p-4 md:p-6 lg:p-8 text-center bg-background">
      <div className="max-w-md w-full mx-auto space-y-6 md:space-y-8">
        <div className="w-48 h-48 md:w-56 md:h-56 lg:w-64 lg:h-64 mx-auto">
          <Lottie 
            animationData={animationData}
            loop={true}
            autoplay={true}
            className="w-full h-full"
          />
        </div>
        <div className="space-y-4 md:space-y-6">
          <div className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-primary">
            {code}
          </div>
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight">
            {title}
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-sm mx-auto">
            {subtitle}
          </p>
          {errorMessage && (
            <div className="mt-4 p-4 rounded-lg bg-destructive/10 text-destructive mx-auto max-w-sm">
              <p className="text-sm md:text-base font-medium">{errorMessage}</p>
              {timestamp && (
                <p className="text-xs md:text-sm mt-2 text-muted-foreground">
                  Occurred at: {new Date(timestamp).toLocaleString()}
                </p>
              )}
            </div>
          )}
        </div>
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          {onRetry && (
            <Button 
              onClick={onRetry}
              className="w-full sm:w-auto px-6 py-2 md:px-8 md:py-3 text-base md:text-lg" 
              size="lg"
            >
              Try Again
            </Button>
          )}
          <motion.div
            whileHover={{ scale: isProcessing ? 1 : 1.02 }}
            whileTap={{ scale: isProcessing ? 1 : 0.98 }}
            transition={{
              type: "spring",
              stiffness: 200,
              damping: 15,
              mass: 1
            }}
            className="w-full sm:w-auto"
          >
            <Button 
              onClick={handleHomeClick}
              className="w-full sm:w-auto px-6 py-2 md:px-8 md:py-3 text-base md:text-lg bg-primary hover:bg-primary/90 transition-all duration-200" 
              size="lg"
              disabled={isProcessing}
            >
              <AnimatePresence mode="wait">
                {isProcessing ? (
                  <motion.span 
                    key="processing"
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -5 }}
                    transition={{
                      duration: 0.6, 
                      ease: [0.43, 0.13, 0.23, 0.96] 
                    }}
                    className="flex items-center justify-center"
                  >
                    <Loader2 className="h-5 w-5 animate-spin" />
                  </motion.span>
                ) : (
                  <motion.span
                    key="default"
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -5 }}
                    transition={{
                      duration: 0.6, 
                      ease: [0.43, 0.13, 0.23, 0.96]
                    }}
                  >
                    Return Home
                  </motion.span>
                )}
              </AnimatePresence>
            </Button>
          </motion.div>
        </div>
      </div>
    </div>
  );
}